import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { Loader2, Plus, Trash2, RefreshCcw, Copy, Check, Wifi, AlertTriangle } from "lucide-react";

/**
 * Devices — provisioning UI for the IoT intake bridge.
 *
 * Each device gets a per-device token. Six pastes that token into the device's
 * webhook config (Tapo plug action / Home Assistant rest_command / iOS
 * Shortcut header). Token is shown ONCE on creation or rotation — anything
 * else would mean storing it in plaintext on the server side, which is the
 * exact thing we're trying to avoid.
 *
 * Backend: /app/backend/routers/intake.py
 */

const DEVICE_TYPES = [
    { value: "ios-shortcut",   label: "iOS Shortcut" },
    { value: "tapo-plug",      label: "Tapo Plug" },
    { value: "lifx-bulb",      label: "LIFX Bulb" },
    { value: "mmwave-radar",   label: "mmWave Radar" },
    { value: "fridge-sensor",  label: "Fridge Sensor" },
    { value: "home-assistant", label: "Home Assistant" },
    { value: "raspberry-pi",   label: "Raspberry Pi" },
    { value: "webhook",        label: "Generic Webhook" },
];

const DEFAULT_DOMAINS = [
    "other", "finance", "health", "diet", "activity", "mood", "medication",
    "post", "pattern", "song", "eulogy", "recipe", "callout",
];

export default function Devices() {
    const [devices, setDevices] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ label: "", device_type: "ios-shortcut", default_domain: "other" });
    const [createdToken, setCreatedToken] = useState(null); // {id,label,token} after provision/rotate
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/shed/intake/devices");
            setDevices(data.items || []);
        } catch (e) {
            setError(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
            setDevices([]);
        }
    };
    useEffect(() => { load(); }, []);

    const submit = async (ev) => {
        ev.preventDefault();
        setError("");
        if (!form.label.trim()) { setError("Label is required."); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/shed/intake/devices", form);
            setCreatedToken({ id: data.id, label: data.label, token: data.token, fresh: true });
            setForm({ label: "", device_type: "ios-shortcut", default_domain: "other" });
            setShowForm(false);
            await load();
        } catch (e) {
            setError(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const rotate = async (d) => {
        if (!window.confirm(`Rotate token for "${d.label}"? The old token stops working immediately.`)) return;
        try {
            const { data } = await api.post(`/shed/intake/devices/${d.id}/rotate`);
            setCreatedToken({ id: data.id, label: d.label, token: data.token, fresh: false });
        } catch (e) {
            setError(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const remove = async (d) => {
        if (!window.confirm(`Delete "${d.label}"? Past events stay in the Ledger; new POSTs will be refused.`)) return;
        try {
            await api.delete(`/shed/intake/devices/${d.id}`);
            await load();
        } catch (e) {
            setError(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const copyToken = async () => {
        try {
            await navigator.clipboard.writeText(createdToken.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (_) {}
    };

    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Passive Intake · Bridge Roster</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                        Devices<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Pair anything that can POST a JSON webhook — plugs, sensors, iOS Shortcuts, Pi
                        nodes. Each gets a private token. Events file straight into the Ledger without
                        going through the Shredder LLM, so no Universal Key spend per ping.
                    </p>
                </div>
                <div className="pencil text-lg transform -rotate-1">"Wire the room, file the room." — No. 2</div>
            </div>

            {error && (
                <div
                    className="paper-card border border-stamp-red/40 p-3 mb-4 font-body text-xs text-stamp-red flex items-center gap-2"
                    data-testid="devices-error"
                >
                    <AlertTriangle className="w-3 h-3" /> {error}
                </div>
            )}

            {/* Newly issued token panel */}
            {createdToken && (
                <div className="paper-card border-2 border-stamp-amber p-4 mb-6" data-testid="devices-token-panel">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="label-typed text-stamp-amber">
                                {createdToken.fresh ? "Token issued — copy now" : "Token rotated — copy now"}
                            </div>
                            <div className="font-body text-xs text-ink-soft mt-1">
                                For <span className="font-heading text-ink">{createdToken.label}</span>.
                                This is the only time the token will be shown. Lose it and you'll have to rotate again.
                            </div>
                        </div>
                        <button
                            onClick={() => setCreatedToken(null)}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                            data-testid="devices-token-dismiss"
                        >
                            Done · hide
                        </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <code
                            className="flex-1 min-w-0 bg-paper-dark/40 border border-ink/20 px-3 py-2 font-mono text-xs text-ink break-all"
                            data-testid="devices-token-value"
                        >
                            {createdToken.token}
                        </code>
                        <button
                            onClick={copyToken}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] border border-ink/30 px-3 py-2 hover:border-stamp-red hover:text-stamp-red inline-flex items-center gap-2"
                            data-testid="devices-token-copy"
                        >
                            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    <div className="mt-3 font-body text-[11px] text-ink-soft">
                        <span className="label-typed">Webhook URL · </span>
                        <code className="font-mono text-ink break-all">
                            POST {backendBase}/api/shed/intake/{createdToken.id}
                        </code>
                        <div className="mt-1">
                            <span className="label-typed">Header · </span>
                            <code className="font-mono text-ink">X-Device-Token: ‹token above›</code>
                        </div>
                        <div className="mt-1">
                            <span className="label-typed">Body · </span>
                            <code className="font-mono text-ink">{`{"summary":"Front door opened","tags":["door","entry"]}`}</code>
                        </div>
                    </div>
                </div>
            )}

            {/* Add device */}
            <div className="paper-card p-4 mb-6" data-testid="devices-add">
                <div className="flex items-center justify-between gap-3">
                    <div className="label-typed">Provision new device</div>
                    <button
                        onClick={() => setShowForm((v) => !v)}
                        className="font-heading uppercase text-[10px] tracking-[0.22em] border border-stamp-red text-stamp-red px-3 py-2 hover:bg-stamp-red hover:text-paper inline-flex items-center gap-2"
                        data-testid="devices-add-toggle"
                    >
                        <Plus className="w-3 h-3" /> {showForm ? "Cancel" : "New device"}
                    </button>
                </div>
                {showForm && (
                    <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="devices-add-form">
                        <label className="block">
                            <span className="label-typed">Label</span>
                            <input
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="Front door plug"
                                maxLength={120}
                                className="w-full mt-1 px-3 py-2 text-sm font-body bg-paper-light border border-ink/30 focus:outline-none focus:border-stamp-red"
                                data-testid="devices-input-label"
                            />
                        </label>
                        <label className="block">
                            <span className="label-typed">Device type</span>
                            <select
                                value={form.device_type}
                                onChange={(e) => setForm({ ...form, device_type: e.target.value })}
                                className="w-full mt-1 px-3 py-2 text-sm font-body bg-paper-light border border-ink/30 focus:outline-none focus:border-stamp-red"
                                data-testid="devices-input-type"
                            >
                                {DEVICE_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="label-typed">Default domain</span>
                            <select
                                value={form.default_domain}
                                onChange={(e) => setForm({ ...form, default_domain: e.target.value })}
                                className="w-full mt-1 px-3 py-2 text-sm font-body bg-paper-light border border-ink/30 focus:outline-none focus:border-stamp-red"
                                data-testid="devices-input-domain"
                            >
                                {DEFAULT_DOMAINS.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </label>
                        <div className="md:col-span-3 flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting || !form.label.trim()}
                                className="font-heading uppercase text-[10px] tracking-[0.22em] bg-stamp-red text-paper px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid="devices-add-submit"
                            >
                                {submitting ? "Provisioning…" : "Provision device"}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Device list */}
            {devices === null ? (
                <div className="flex items-center gap-2 text-ink-soft font-heading text-sm uppercase tracking-[0.3em]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Reading the wiring diagram...
                </div>
            ) : devices.length === 0 ? (
                <div className="paper-card border border-dashed border-ink/30 p-16 text-center tilt-r" data-testid="devices-empty">
                    <div className="font-heading text-3xl uppercase tracking-[0.06em] text-ink-faint">
                        No devices paired
                    </div>
                    <div className="label-typed mt-3">
                        Provision your first webhook above and start streaming passive signal in.
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="devices-list">
                    {devices.map((d) => {
                        const last = d.last_event_at ? d.last_event_at.replace("T", " ").slice(0, 16) : "—";
                        const status = d.status === "active";
                        return (
                            <div key={d.id} className="paper-card p-4" data-testid={`device-${d.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Wifi className={`w-3.5 h-3.5 ${status ? "text-stamp-green" : "text-ink-soft"}`} />
                                            <div className="font-heading text-base text-ink uppercase tracking-[0.06em] truncate">
                                                {d.label}
                                            </div>
                                        </div>
                                        <div className="label-typed flex flex-wrap gap-x-3 gap-y-1">
                                            <span>{d.device_type}</span>
                                            <span>· default {d.default_domain}</span>
                                            <span>· {d.event_count || 0} events</span>
                                            <span>· last {last}</span>
                                        </div>
                                        <div className="font-mono text-[10px] text-ink-soft mt-2 break-all">{d.id}</div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            onClick={() => rotate(d)}
                                            className="text-ink-soft hover:text-stamp-amber p-1"
                                            data-testid={`device-rotate-${d.id}`}
                                            title="Rotate token"
                                        >
                                            <RefreshCcw className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => remove(d)}
                                            className="text-ink-soft hover:text-stamp-red p-1"
                                            data-testid={`device-delete-${d.id}`}
                                            title="Delete device"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Shell>
    );
}
